import React, { FC, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useScope } from 'contexts/scopeContext';
import { ButtonClean } from 'components/_new/Button';
import { Menu, MenuItem } from 'components/_new/Menu';
import { OverlayElement } from 'components/_new/Overlay';
import { Avatar } from 'components/_new/Avatar';
import { userDisplayName } from 'utils/userDisplayName';
import { CurrentUser } from 'types/CurrentUser';
import { useAuth } from 'contexts/authContext';
import { useGlobal } from 'contexts/globalContext';
import { OrganizationRole, ProgramRole } from 'types/Membership';
import { usersApi } from 'api';
import { useProgramTranslationStatic } from 'hooks/useProgramTranslation';
import { usePanel } from 'contexts/panelContext';
import { hasMultipleProgramMemberships } from 'utils/hasMultipleProgramMemberships';

interface Props {
	currentUser: CurrentUser;
	setShowOrgDialog: React.Dispatch<React.SetStateAction<boolean>>;
	source: 'panel' | 'landingPage' | 'userPanel';
	hasApplicationSubmitted?: boolean;
	optionsLanguage: string;
}

const UserSettings: FC<Props> = (props) => {
	const { t } = useTranslation(['common']);
	const navigate = useNavigate();
	const { i18n } = useTranslation();
	const { organizationName, programName, type: panelType } = useParams();

	const { setMyAccountModalOpen, setMyCalendarModalOpen } = useGlobal();
	const { logout, currentUserRefetch } = useAuth();
	const { hasPanelAccess, currentProgram } = useScope();
	const panelContext = usePanel();

	const { currentUser, setShowOrgDialog, source, hasApplicationSubmitted, optionsLanguage } = props;
	const isLandingPage = source === 'landingPage';
	const isInUserPanel = source === 'userPanel';
	const isInPanel = source === 'panel';
	const hasAccessToPanel =
		(isLandingPage && !!programName && hasApplicationSubmitted) || (!isInUserPanel && isInPanel);

	const currentLanguage = currentUser?.language || (isLandingPage ? optionsLanguage : i18n.resolvedLanguage);
	const { tProgram } = useProgramTranslationStatic({
		programContext: currentProgram?.programType,
		lng: currentLanguage,
	});
	const getLabel = (translation: string) => tProgram(translation);

	const switchPanelType = panelType === 'mentor' ? 'mentee' : 'mentor';
	const canSwitchUserPanel = Boolean(isInUserPanel && panelType && panelContext?.canSwitchPanels);
	const switchPanelPath = `/panel/${organizationName}/${programName}/${switchPanelType}`;
	const switchPanelLabel = switchPanelType === 'mentor' ? getLabel('misc.mentors') : getLabel('misc.mentees');
	const hasMultiplePrograms = hasMultipleProgramMemberships(currentUser.programMemberships);

	const hasOrganizationAdminPrivilage =
		currentUser &&
		currentProgram &&
		currentUser.organizationMemberships.find(
			({ organizationId }) => currentProgram.organizationId === organizationId
		)?.role === OrganizationRole.ORGANIZATION_ADMIN;

	const hasProgramAdminPrivilage =
		hasOrganizationAdminPrivilage ||
		(currentUser &&
			currentProgram &&
			currentUser.programMemberships.find(({ programId }) => currentProgram.id === programId)?.role ===
				ProgramRole.PROGRAM_ADMIN);

	const isPairsCoordinator =
		currentUser &&
		currentProgram &&
		currentUser.programMemberships.find(({ programId }) => currentProgram.id === programId)?.role ===
			ProgramRole.PAIRS_COORDINATOR;

	const options = useMemo(() => {
		const items = [
			canSwitchUserPanel && {
				key: 'switchPanel',
				type: 'link',
				label: `${getLabel('userPanel.goTo')} ${switchPanelLabel}`,
				to: {
					pathname: switchPanelPath,
				},
				itemClassName: 'block md:hidden',
			},
			hasOrganizationAdminPrivilage && {
				key: 'orgAdmin',
				type: 'button',
				label: getLabel('landingPages.buttons.organizationAdministration'),
				onClick: () => navigate(`/organization/${organizationName}`),
			},
			hasProgramAdminPrivilage && {
				key: 'progAdmin',
				type: 'button',
				label: getLabel('landingPages.buttons.programAdministration'),
				onClick: () => navigate(`/program/${organizationName}/${programName}`),
			},
			isPairsCoordinator && {
				key: 'pairsCoord',
				type: 'button',
				label: getLabel('landingPages.buttons.pairsCoordinate'),
				onClick: () => navigate(`/coordinator/${organizationName}/${programName}`),
			},
			hasMultiplePrograms && {
				key: 'changeOrganization',
				type: 'button',
				label: getLabel('landingPages.buttons.changeOrganization'),
				onClick: () => setShowOrgDialog(true),
			},
			{
				key: 'myAccount',
				type: 'button',
				label: getLabel('landingPages.buttons.myAccount'),
				onClick: () => {
					setMyAccountModalOpen(true);
				},
			},
			{
				key: 'myCalendar',
				type: 'button',
				label: getLabel('landingPages.buttons.myCalendar'),
				onClick: () => {
					setMyCalendarModalOpen(true);
				},
			},
			{
				key: 'toggleProfessionalView',
				type: 'button',
				label: !currentUser?.isProfessionalViewEnabled
					? getLabel('userPanel.settings.showProfessionalView')
					: getLabel('userPanel.settings.hideProfessionalView'),
				onClick: () => {
					usersApi
						.editMyData({ isProfessionalViewEnabled: !currentUser?.isProfessionalViewEnabled })
						.then(() => {
							currentUserRefetch();
						});
				},
			},
			{
				key: 'logout',
				type: 'button',
				label: getLabel('landingPages.buttons.logout'),
				onClick: () => logout(),
			},
		] as MenuItem[];

		if (hasAccessToPanel && hasPanelAccess) {
			items.splice(0, 0, {
				key: 'goToPanel',
				type: 'button',
				label: getLabel('landingPages.buttons.goToPanel'),
				onClick: () => navigate(`/panel/${organizationName}/${programName}`),
			});
		}

		return items.filter(Boolean);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		canSwitchUserPanel,
		currentUser?.isProfessionalViewEnabled,
		currentLanguage,
		hasPanelAccess,
		hasOrganizationAdminPrivilage,
		hasProgramAdminPrivilage,
		hasMultiplePrograms,
		hasAccessToPanel,
		isPairsCoordinator,
		switchPanelLabel,
		switchPanelPath,
	]);

	const menuRef = useRef<OverlayElement>(null);

	const displayName = userDisplayName(currentUser, null);

	return (
		<>
			<ButtonClean
				aria-label={t('landingPages.buttons.userSettings')}
				className="rounded-xl"
				onClick={({ currentTarget }) => {
					menuRef?.current?.toggle(currentTarget);
				}}
			>
				<Avatar id={currentUser.id} size="md" src={currentUser.avatar} name={displayName} />
			</ButtonClean>
			<Menu ref={menuRef} items={options} variant="bigger" placement="bottom-end" />
		</>
	);
};

export default UserSettings;
